import { useMemo } from 'react';
import useDashboardData from './hooks/useDashboardData';
import { mapSimEvent, mapCourseEvent, mapReminderEvent } from './data';
import { hasTenancyScope } from './serverScope';

const MAPPERS = { simulations: mapSimEvent, courses: mapCourseEvent, reminders: mapReminderEvent };

function useTenancyEvents(metric, { scope, period, startDate, endDate }) {
    const scoped = hasTenancyScope(scope);
    const tenancy = scoped ? (Array.isArray(scope.tenancy) ? scope.tenancy : [scope.tenancy]) : null;
    const q = useDashboardData(
        { metric, aggregation: 'events', period, startDate, endDate, scope: { tenancy } },
        { skip: !scoped },
    );
    const records = q.data?.records;
    const rows = useMemo(() => {
        if (!scoped || !Array.isArray(records)) return null;
        const out = records.map(MAPPERS[metric]).filter((e) => e.dateSent);
        out.sort((a, b) => (a.dateSent < b.dateSent ? 1 : -1));
        return out;
    }, [scoped, records, metric]);
    return { scoped, rows, isLoading: scoped && !Array.isArray(records) && (q.isFetching || q.isLoading), isError: scoped && q.isError };
}

export function useTenancySimEvents(args) {
    return useTenancyEvents('simulations', args);
}

export function useTenancyCourseEvents(args) {
    return useTenancyEvents('courses', args);
}

function useUserEventRows(metric, { email, period, startDate, endDate, skip }) {
    const q = useDashboardData(
        { metric, aggregation: 'events', period, startDate, endDate, scope: { user: [email] } },
        { skip },
    );
    const records = q.data?.records;
    return useMemo(() => {
        if (skip || !Array.isArray(records)) return null;
        return records.map(MAPPERS[metric]).filter((e) => e.dateSent);
    }, [skip, records, metric]);
}

export function useUserEvents({ email, period, startDate, endDate, skip = false }) {
    const args = { email, period, startDate, endDate, skip };
    return {
        sims: useUserEventRows('simulations', args),
        courses: useUserEventRows('courses', args),
        reminders: useUserEventRows('reminders', args),
    };
}
